import { Component, Input } from '@angular/core';
import { Observable } from 'rxjs';
import { AgentStepModel, StepType } from 'src/app/models/api/agentStep.model';
import { Evidence } from 'src/app/models/api/finding.model';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import {
  ActionGetAgentStepSensitiveDataResponseModel,
  AiAgentStepsHttpService,
  SensitiveDataRequestActions,
} from 'src/app/services/httpServices/detection/aiAgentStepsHttpService/ai-agent-steps-http.service';
import { SidePanelService } from '../../../features/side-panel/services/side-panel.service';

@Component({
  selector: 'app-evidence-list',
  templateUrl: './evidence-list.component.html',
  styleUrls: ['./evidence-list.component.scss'],
})
export class EvidenceListComponent {
  @Input() public evidenceList: Evidence[] = [];
  @Input() public relatedSteps: AgentStepModel[];
  private readonly _locationSeparator = ' > ';
  private readonly _sensitiveSourceKey = 'sensitiveSource';
  private readonly _sensitiveContentStartIndexesKey = 'sensitiveContentStartIndexes';

  constructor(
    private readonly _beautifyDisplayStringPipe: BeautifyDisplayStringPipe,
    private readonly _aiAgentStepsService: AiAgentStepsHttpService,
    private readonly _labelsProviderPipe: LabelsProviderPipe,
    private readonly _sidePanelService: SidePanelService
  ) {}

  public getStepType(evidence: Evidence): StepType | undefined {
    const stepDetails = this.getStepDetailsFromEvidence(evidence);
    if (!stepDetails) {
      return undefined;
    }
    return stepDetails.type;
  }

  public getLocation(evidence: Evidence): string {
    let result = '';
    const splitLocation = evidence.location.split('.');
    if (splitLocation.length > 0) {
      const formattedString = this._beautifyDisplayStringPipe.transform(splitLocation[splitLocation.length - 1]);
      result += `${this._locationSeparator}${formattedString}`;
    }
    return result;
  }

  public isSensitiveContent(evidence: Evidence): boolean {
    return (
      evidence.additionalData[this._sensitiveContentStartIndexesKey] !== undefined ||
      evidence.additionalData[this._sensitiveSourceKey] !== undefined
    );
  }

  public getEvidenceSensitiveIndexes(evidence: Evidence): string | undefined {
    return evidence.additionalData[this._sensitiveContentStartIndexesKey];
  }

  public getSensitiveDataMethod(evidence: Evidence): () => Observable<ActionGetAgentStepSensitiveDataResponseModel> {
    const stepDetails = this.getStepDetailsFromEvidence(evidence);
    if (!stepDetails) {
      return () =>
        this._aiAgentStepsService.getAgentStepSensitiveData(
          this.relatedSteps[0].integration.id,
          this.relatedSteps[0].id,
          SensitiveDataRequestActions.transcript
        );
    }
    const sensitiveRequest: string | undefined = evidence.additionalData[this._sensitiveSourceKey];
    if (sensitiveRequest && this.isSensitiveValueValid(sensitiveRequest)) {
      const type = SensitiveDataRequestActions[sensitiveRequest];
      return () =>
        this._aiAgentStepsService.getAgentStepSensitiveData(stepDetails.integration.id, stepDetails.id, type);
    }
    switch (stepDetails.type) {
      case StepType.userMessageStep:
      case StepType.aiMessageStep:
        return () =>
          this._aiAgentStepsService.getAgentStepSensitiveData(
            stepDetails.integration.id,
            stepDetails.id,
            SensitiveDataRequestActions.transcript
          );
      case StepType.toolInvocationStep:
        return () =>
          this._aiAgentStepsService.getAgentStepSensitiveData(
            stepDetails.integration.id,
            stepDetails.id,
            SensitiveDataRequestActions.invocationResult
          );
      case StepType.ragStep:
        return () =>
          this._aiAgentStepsService.getAgentStepSensitiveData(
            stepDetails.integration.id,
            stepDetails.id,
            SensitiveDataRequestActions.snippet
          );
      case StepType.triggerStep:
        return () =>
          this._aiAgentStepsService.getAgentStepSensitiveData(
            stepDetails.integration.id,
            stepDetails.id,
            SensitiveDataRequestActions.payload
          );
      default:
        return () =>
          this._aiAgentStepsService.getAgentStepSensitiveData(
            this.relatedSteps[0].integration.id,
            this.relatedSteps[0].id,
            SensitiveDataRequestActions.transcript
          );
    }
  }

  public getCoreEvidence(evidence: Evidence): string {
    // Find anything between < and >
    let coreEvidence = evidence.coreEvidence;
    const pattern = /<([^>]+)>/g;
    if (this.isSensitiveContent(evidence)) {
      if (!evidence.coreEvidence.toLowerCase().includes('fetch content')) {
        coreEvidence += ' ' + this._labelsProviderPipe.transform('evidenceListPage.sensitiveContentEvidence');
      }
    }
    return coreEvidence.replace(pattern, '<strong>$1</strong>');
  }

  public openEvidence(evidence: Evidence) {
    const stepDetails = this.getStepDetailsFromEvidence(evidence);
    if (!stepDetails) {
      return;
    }
    this._sidePanelService.openAgentStepDetailsFromFinding(stepDetails.thread.id, stepDetails.id);
  }

  private getStepDetailsFromEvidence(evidence: Evidence): AgentStepModel | undefined {
    return this.relatedSteps.find((step) => step.id === evidence.stepId);
  }

  private isSensitiveValueValid(val: string): val is keyof typeof SensitiveDataRequestActions {
    return Object.keys(SensitiveDataRequestActions).includes(val);
  }
}
